import { Icon, type IconName } from "../components/Icon";
import { defineStrings, formatNumber, type Locale } from "../i18n";
import { pluralForm, translator } from "./shared";

const strings = defineStrings(
  {
    frustrationZero: "No moments of frustration this week",
    frustrationOne: "{count} moment of frustration this week",
    frustrationOther: "{count} moments of frustration this week",
    plateauZero: "No skills on a plateau this week",
    plateauOne: "{count} skill on a plateau this week",
    plateauOther: "{count} skills on a plateau this week",
    daily_capZero: "Daily time limit not reached this week",
    daily_capOne: "Daily time limit reached on {count} day this week",
    daily_capOther: "Daily time limit reached on {count} days this week",
    safetyZero: "No safety alerts this week",
    safetyOne: "{count} safety alert this week",
    safetyOther: "{count} safety alerts this week",
    noData: "No data yet",
  },
  {
    frustrationZero: "Aucun moment de frustration cette semaine",
    frustrationOne: "{count}\u00a0moment de frustration cette semaine",
    frustrationOther: "{count}\u00a0moments de frustration cette semaine",
    plateauZero: "Aucune compétence ne stagne cette semaine",
    plateauOne: "{count}\u00a0compétence stagne cette semaine",
    plateauOther: "{count}\u00a0compétences stagnent cette semaine",
    daily_capZero: "Limite de temps quotidienne jamais atteinte cette semaine",
    daily_capOne: "Limite de temps quotidienne atteinte {count}\u00a0jour cette semaine",
    daily_capOther: "Limite de temps quotidienne atteinte {count}\u00a0jours cette semaine",
    safetyZero: "Aucune alerte de sécurité cette semaine",
    safetyOne: "{count}\u00a0alerte de sécurité cette semaine",
    safetyOther: "{count}\u00a0alertes de sécurité cette semaine",
    noData: "Pas encore de données",
  },
);

export type AlertReason = "frustration" | "plateau" | "daily_cap" | "safety";

export interface AlertCountFigureProps {
  readonly reason: AlertReason;
  /** From the engine: how many times this week. Rounded down to a whole, non-negative count. */
  readonly count: number;
  readonly locale: Locale;
}

const icons: Readonly<Record<AlertReason, IconName>> = {
  frustration: "heart",
  plateau: "steady",
  daily_cap: "info",
  safety: "alert",
};

/** A localised sentence counting one kind of alert this week, with plural rules. */
export function AlertCountFigure(props: AlertCountFigureProps) {
  const t = translator(strings, props.locale);
  const count = Number.isFinite(props.count) ? Math.max(0, Math.floor(props.count)) : null;
  const sentence =
    count === null
      ? t("noData")
      : count === 0
        ? t(`${props.reason}Zero` as const)
        : pluralForm(props.locale, count) === "one"
          ? t(`${props.reason}One` as const, { count: formatNumber(props.locale, count) })
          : t(`${props.reason}Other` as const, { count: formatNumber(props.locale, count) });
  return (
    <p
      className="p-alert"
      data-reason={props.reason}
      data-zero={count === 0 || count === null ? "" : undefined}
    >
      <Icon name={icons[props.reason]} size={18} className="p-alert__icon" />
      <span>{sentence}</span>
    </p>
  );
}
